/* CreateOrgRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const TextAreaControl = uiCore.Control.Textfield.Textarea;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const SingleCombobox = uiCore.Control.OptionSelection.Single.ComboBox;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;

const LDAPComponent = require("../ldapConfig/LdapConfigComponent");
const RADIUSComponent = require("../radiusConfig/RadiusConfigComponent");
const ErrorMapTranslation = require("../../../commoncomponents/ErrorMapTranslation").Organization;

class CreateOrgRenderer {
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("create-gl");
        this._rootElem.classList.add("screen");
        this._rootElem.component = this._model;

        this._createFormElem = document.createElement("create-form-gl");
        this._createFormElem.classList.add("screen-form");
        this._rootElem.appendChild(this._createFormElem);

        this._name = new InputTextControl({darkTheme: true});
        this._description = new TextAreaControl({darkTheme: true});
        this._provider = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
            selectionUnselectable: true,
        });

        this._distAreaSelection = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Create private and shared Distribution Areas",
        });
        this._distLabVCEnabled = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable use of Git repositories in Solutions Manager",
        });

        this._renderPair("Name *", this._name);
        this._renderPair("Description", this._description);
        this._renderPair("Provider *", this._provider);

        this._authMechRadio = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
            options: [
                {
                    label: "Gluware",
                    value: "gluware",
                },
                {
                    label: "LDAP",
                    value: "ldap",
                },
                {
                    label: "RADIUS",
                    value: "radius",
                },
            ],
        });

        this._renderPair("User Authentication Mechanism", this._authMechRadio);

        this._ldapComponent = new LDAPComponent({
            orgInfo: this.model.createModel,
            userInfo: this.model.userInfo,
            isProvider: true,
            credentialsList: this._model._credentialsList,
        });
        this._createFormElem.appendChild(this._ldapComponent.renderer.rootElement);

        this._radiusComponent = new RADIUSComponent({
            orgInfo: this.model.createModel,
            userInfo: this.model.userInfo,
            isProvider: true,
            credentialsList: this._model._credentialsList,
        });
        this._createFormElem.appendChild(this._radiusComponent.renderer.rootElement);

        // Distribution center
        this._createFormElem.appendChild(document.createElement("hr"));
        this._renderLabel("Distribution Center (Advanced)");
        if (this._model._isDCEnabled) {
            this._renderMono(this._distAreaSelection);
        }
        this._renderMono(this._distLabVCEnabled);

        this._buttonGroupContainer = document.createElement("button-group-container");
        this._buttonGroupContainer.classList.add("button-group");
        this._cancelButton = new ButtonControl({id: "cancel", label: "Cancel", disabled: false});
        this._createButton = new ButtonControl({id: "create", label: "Create", disabled: true});
        this._buttonGroupContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._buttonGroupContainer.appendChild(this._createButton.renderer.getRootElement());
        this._rootElem.appendChild(this._buttonGroupContainer);

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();

        let promArr = [];

        promArr.push(
            this._name.shutdown(),
            this._description.shutdown(),
            this._distAreaSelection.shutdown(),
            this._distLabVCEnabled.shutdown(),
            this._provider.shutdown(),
            this._createButton.shutdown(),
            this._cancelButton.shutdown(),
            this._ldapComponent.shutdown(),
            this._radiusComponent.shutdown(),
            this._authMechRadio.shutdown()
        );

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._name = undefined;
                this._description = undefined;
                this._provider = undefined;
                this._distAreaSelection = undefined;
                this._distLabVCEnabled = undefined;
                this._cancelButton = undefined;
                this._createButton = undefined;
                this._buttonGroupContainer.remove();
                this._buttonGroupContainer = undefined;
                this._authMechRadio = undefined;
                this._ldapComponent = undefined;
                this._radiusComponent = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                console.log("Error shutting down CreateOrgRenderer: ", err);
            }
        );
    }

    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("CreateModelUpdated", (this._makeDirty = this._makeDirty.bind(this)));
        this._model.on("CreateModelCleared", (this.render = this.render.bind(this)));
        this._model.on("CreateOrgError", (this._createOrgError = this._createOrgError.bind(this)));
        this._model.on("TestLdapError", (this._testLdapError = this._testLdapError.bind(this)));
        this._model.on(
            "TestRadiusError",
            (this._testRadiusError = this._testRadiusError.bind(this))
        );
        this._name.on("UserValue", (this._nameChange = this._nameChange.bind(this)));
        this._description.on(
            "UserValue",
            (this._descriptionChange = this._descriptionChange.bind(this))
        );
        this._distAreaSelection.on(
            "UserValue",
            (this._distAreaSelectionChange = this._distAreaSelectionChange.bind(this))
        );
        this._distLabVCEnabled.on(
            "UserValue",
            (this._distLabVCEnabledChange = this._distLabVCEnabledChange.bind(this))
        );
        this._provider.on("UserValue", (this._providerChange = this._providerChange.bind(this)));
        this._createButton.on("UserClick", (this._callCreateOrg = this._callCreateOrg.bind(this)));
        this._cancelButton.on(
            "UserClick",
            (this._cancelCreateOrg = this._cancelCreateOrg.bind(this))
        );
        this._authMechRadio.on(
            "UserValue",
            (this._authMechChange = this._authMechChange.bind(this))
        );
        this._ldapComponent.on(
            "ModelUpdated",
            (this._onLdapModelChanged = this._onLdapModelChanged.bind(this))
        );
        this._ldapComponent.on("testLDAP", (this._onTestLdap = this._onTestLdap.bind(this)));
        this._radiusComponent.on(
            "ModelUpdated",
            (this._onRadiusModelChanged = this._onRadiusModelChanged.bind(this))
        );
        this._radiusComponent.on(
            "testRADIUS",
            (this._onTestRadius = this._onTestRadius.bind(this))
        );
    }

    _unregisterListeners() {
        this._model
            .removeAllListeners("Shutdown")
            .removeAllListeners("CreateModelUpdated")
            .removeAllListeners("CreateModelCleared")
            .removeAllListeners("CreateOrgError")
            .removeAllListeners("TestLdapError")
            .removeAllListeners("TestRadiusError");
        this._authMechRadio.removeListener("UserValue", this._authMechChange);
        this._ldapComponent.removeListener("ModelUpdated", this._onLdapModelChanged);
        this._ldapComponent.removeListener("testLDAP", this._onTestLdap);
        this._radiusComponent.removeListener("ModelUpdated", this._onRadiusModelChanged);
        this._radiusComponent.removeListener("testRADIUS", this._onTestRadius);
        this._name.removeAllListeners("UserValue");
        this._description.removeAllListeners("UserValue");
        this._distAreaSelection.removeAllListeners("UserValue");
        this._distLabVCEnabled.removeAllListeners("UserValue");
        this._provider.removeAllListeners("UserValue");
        this._createButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
    }

    _renderLabel(labelName) {
        const labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.classList.add("form-label-gl");
        this._createFormElem.appendChild(labelElem);
    }

    _renderPair(labelName, component) {
        this._renderLabel(labelName);
        this._createFormElem.appendChild(component.renderer.getRootElement());
    }

    _renderMono(component) {
        this._createFormElem.appendChild(component.renderer.getRootElement());
    }

    _callCreateOrg() {
        this._uiUtils.writeMessage("Creating Organization...");
        this._disableButtons();
        this.model.createOrg();
    }

    _testLdapError(err) {
        this._uiUtils.writeErrorMessage("Test Error: " + err.errorCode);
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    Object.keys(appErr.errorDetails).forEach((propNameErr) => {
                        let invalidText = ErrorMapTranslation.default_entry;
                        if (_.get(ErrorMapTranslation, propNameErr, false)) {
                            invalidText = _.get(ErrorMapTranslation, propNameErr);
                        }
                        this._uiUtils.writeErrorMessage(invalidText + ". ");
                        console.log("Error: " + invalidText, appErr.errorDetails);
                    });
                }
                break;
            default:
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                break;
        }
    }

    _testRadiusError(err) {
        this._uiUtils.writeErrorMessage("Test Error:");
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    Object.keys(appErr.errorDetails).forEach((propNameErr) => {
                        let invalidText = ErrorMapTranslation.default_entry;
                        if (_.get(ErrorMapTranslation, propNameErr, false)) {
                            invalidText = _.get(ErrorMapTranslation, propNameErr);
                        }
                        this._uiUtils.writeErrorMessage(invalidText + ". ");
                        console.log("Error: " + invalidText, appErr.errorDetails);
                    });
                }
                break;
            default:
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                break;
        }
    }

    _createOrgError(err) {
        this._uiUtils.clearMessage();
        this._uiUtils.writeErrorMessage("Organization creation error: ");
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    const errorNames = Object.keys(appErr.errorDetails);
                    errorNames.forEach((propNameErr, index) => {
                        let invalidText = ErrorMapTranslation.default_entry;
                        if (ErrorMapTranslation.hasOwnProperty(propNameErr)) {
                            invalidText = ErrorMapTranslation[propNameErr];
                        }
                        if (index === errorNames.length - 2) {
                            invalidText = invalidText + " and";
                        } else if (index !== errorNames.length - 1) {
                            invalidText = invalidText + ",";
                        }
                        this._uiUtils.writeErrorMessage(invalidText);
                    });
                }
                break;
            case "DistributionCenterServiceError":
                appErr.errorMsg = "Unable to access the Gluware Distribution Center";
                this._uiUtils.writeErrorMessage(appErr.errorMsg);
                this._distAreaSelection.setValue(false);
                this._distAreaSelection.emit("UserValue", false);
                break;
            default:
                if (appErr.errorMsg) {
                    this._uiUtils.writeErrorMessage(appErr.errorMsg);
                } else {
                    this._uiUtils.writeErrorMessage("unknown error");
                }
                break;
        }
        this._makeDirty();
    }

    _cancelCreateOrg() {
        this._disableButtons();
        this._uiUtils.clearMessage();
        this.model.changeScreen("edit");
    }

    _disableButtons() {
        this._createButton.setDisabled(true);
        this._cancelButton.setDisabled(true);
    }

    _makeDirty() {
        this._createButton.setDisabled(false);
        this._cancelButton.setDisabled(false);
    }

    _makePristine() {
        this._createButton.setDisabled(true);
        this._cancelButton.setDisabled(false);
    }

    _nameChange(val) {
        this.model.updateCreateModelField("name", val);
    }

    _descriptionChange(val) {
        this.model.updateCreateModelField("description", val);
    }

    _distAreaSelectionChange(val) {
        this.model.updateCreateModelField("labEnabled", val);
    }

    _distLabVCEnabledChange(val) {
        this.model.updateCreateModelField("labVersionControlEnabled", val);
    }

    _providerChange(val) {
        this._ldapComponent.emit("ProviderUpdated", val);
        this._radiusComponent.emit("ProviderUpdated", val);
        this.model.updateCreateModelField("providerId", val);
    }

    _renderProviderList() {
        let selectedOrg = this._model.selectedOrg;
        let toBeSelectedOrg;
        let provOpts = [];
        Object.keys(this.model.allOrgs).forEach((orgId) => {
            let option = {
                label: this.model.allOrgs[orgId].name,
                value: orgId,
            };
            provOpts.push(option);
            if (selectedOrg.id === orgId) {
                toBeSelectedOrg = option;
            }
        });
        provOpts = _.sortBy(provOpts, [
            function (option) {
                return option.label.toLowerCase();
            },
        ]);
        this._provider.options = provOpts;
        this._provider.value = toBeSelectedOrg.value || provOpts[0].value;
        this._providerChange(toBeSelectedOrg.value || provOpts[0].value);
        this._ldapComponent.emit("ProviderUpdated", this._provider.value);
        this._radiusComponent.emit("ProviderUpdated", this._provider.value);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._renderProviderList();
        this._ldapComponent.setSelectedOrgInfo(this.model.createModel);
        this._radiusComponent.setSelectedOrgInfo(this.model.createModel);
        this._makePristine();
        this._authMechRadio.setValue("gluware");
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    _authMechChange(authMech) {
        this.model.updateCreateModelField("authMethod", authMech);
        switch (authMech) {
            case "ldap":
                this._radiusComponent.setEnabled(false);
                this._ldapComponent.setEnabled(true);
                break;
            case "radius":
                this._ldapComponent.setEnabled(false);
                this._radiusComponent.setEnabled(true);
                break;
            default: // gluware and any other options
                this._ldapComponent.setEnabled(false);
                this._radiusComponent.setEnabled(false);
        }
    }

    _onLdapModelChanged() {
        let ldapConfig = this._ldapComponent.getSaveModel();
        if (ldapConfig) {
            this.model.updateCreateModelField("ldap", ldapConfig);
        } else {
            this.model.removeCreateModelField("ldap");
        }
    }

    _onTestLdap(data) {
        this.model.emitTestLdap(data);
    }

    _onRadiusModelChanged() {
        let radiusConfig = this._radiusComponent.getSaveModel();
        if (radiusConfig) {
            this.model.updateCreateModelField("radius", radiusConfig);
        } else {
            this.model.removeCreateModelField("radius");
        }
    }

    _onTestRadius(data) {
        this.model.emitTestRadius(data);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = CreateOrgRenderer;
